import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import Grid from '@mui/material/Grid';
import DataGrid from '../../../components/DataGrid/DataGrid';
import { hoursColDef } from '../../../utils/reportingUtils/datagridUtils';
import { useGetAcaAverageHoursGridData } from '../../requests/queries';
import { getMuiFormattedFilterRequest } from '../../../utils/reportingUtils/filterUtils';
import { ApproachingACAThresholdAverageHoursWorkedRow } from '../../types';
import { ACAThresholdFilters } from '../ACAThresholdReportFilters';
import useCfcOrg from '../../../utils/hooks/useCfcOrg';
import useSiteColumnVisibility from '../../../hooks/useSiteColumnVisibility';

export interface ACAAverageHoursDataGridProps {
  filters?: ACAThresholdFilters;
}

function ACAAverageHoursDataGrid({ filters }: Readonly<ACAAverageHoursDataGridProps>) {
  const { t } = useTranslation();

  const { isCfcOrg } = useCfcOrg();

  const { columnVisibilityModel, onColumnVisibilityModelChange } = useSiteColumnVisibility(filters, 'siteName', {
    employeePosId: isCfcOrg,
  });

  const { gridData, isLoading, setACAAverageHoursRequest } = useGetAcaAverageHoursGridData();

  useEffect(() => {
    if (filters) {
      const { sites, datePickerNav } = filters;

      const weekStart = datePickerNav.locale('schedule-offset').startOf('week').format('YYYY-MM-DD');
      const weekEnd = datePickerNav.locale('schedule-offset').endOf('week').format('YYYY-MM-DD');
      const { validRequest, request } = getMuiFormattedFilterRequest(weekStart, weekEnd, sites);

      if (validRequest) {
        setACAAverageHoursRequest(request);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, setACAAverageHoursRequest, isCfcOrg]);

  // grid
  const actualHoursKeys = useMemo(
    () => (!isLoading && gridData && gridData.results.length > 0 ? Object.keys(gridData.results[0].actualHours) : []),
    [gridData, isLoading]
  );

  const weekColumns: GridColDef[] = actualHoursKeys.map((key: string) => ({
    field: `actualHours-week${key}`,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueGetter: (value: string, row: ApproachingACAThresholdAverageHoursWorkedRow) => row.actualHours[key],
    headerName: t('labor.approachingACA.averageHours.week', { week: key }),
    minWidth: 150,
    ...hoursColDef,
  }));

  const averageHoursCommonProp = { flex: 1, minWidth: 125 };
  const averageHoursColumns: GridColDef[] = [
    {
      field: 'siteName',
      headerName: t('labor.approachingACA.site'),
      ...averageHoursCommonProp,
    },
    {
      field: 'employeeName',
      headerName: t('labor.approachingACA.name'),
      ...averageHoursCommonProp,
    },
    {
      field: 'employeePosId',
      headerName: t('labor.approachingACA.employeePosId'),
      ...averageHoursCommonProp,
      minWidth: 185,
    },
    {
      field: 'averageHoursWorked',
      headerName: t('labor.approachingACA.averageHours.averageHours'),
      ...averageHoursCommonProp,
      minWidth: 185,
      ...hoursColDef,
    },
    ...weekColumns,
  ];

  return (
    <Grid container paddingBottom={3} direction='column' gap={3} sx={{ maxWidth: '100%' }}>
      <DataGrid
        columns={averageHoursColumns}
        rows={gridData?.results ?? []}
        pageSizeOptions={[5, 10, 25]}
        loading={isLoading}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        hideFromColumnSelector={isCfcOrg ? ['siteName'] : ['siteName', 'employeePosId']}
      />
    </Grid>
  );
}

export default ACAAverageHoursDataGrid;
